import toolsIndexDeJson from '@/data/toolsIndex.de.json'
import toolsIndexEnJson from '@/data/toolsIndex.en.json'
import type { ToolsIndex, ToolDbEntry } from '@/lib/types'
import { getTechnique, techniqueUrl } from '@/lib/content'
import { getLang, getStrings, type Lang } from '@/i18n'

export const toolsIndexDe = toolsIndexDeJson as ToolsIndex
export const toolsIndexEn = toolsIndexEnJson as ToolsIndex
/** @deprecated use toolsIndexFor */
export const toolsIndex = toolsIndexDe

export function toolsIndexFor(lang?: Lang): ToolsIndex {
  const l = lang || getLang()
  return l === 'en' ? toolsIndexEn : toolsIndexDe
}

export function getAllTools(lang?: Lang): ToolDbEntry[] {
  return toolsIndexFor(lang).tools
}

export function getTool(slug: string, lang?: Lang): ToolDbEntry | undefined {
  return toolsIndexFor(lang).tools.find((t) => t.slug === slug || t.id === slug)
}

export function getToolsByCategory(categoryId: string, lang?: Lang): ToolDbEntry[] {
  return toolsIndexFor(lang).tools.filter((t) => t.category === categoryId || t.kind === categoryId)
}

export function toolUrl(slug: string, lang?: Lang): string {
  const l = lang || getLang()
  return `/${l}/tools/${slug}`
}

function normalizeToolName(s: string): string {
  return String(s || '')
    .toLowerCase()
    .replace(/\(.*?\)/g, ' ')
    .replace(/[^a-z0-9+]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function resolveToolSlug(name: string, lang?: Lang): string | undefined {
  const n = normalizeToolName(name)
  if (!n) return undefined
  const map = toolsIndexFor(lang).aliasMap || {}
  if (map[n]) return map[n]
  for (const [alias, slug] of Object.entries(map)) {
    if (n === alias || n.includes(alias) || alias.includes(n)) return slug
  }
  return undefined
}

export function getRelatedTechniqueLinks(tool: ToolDbEntry, lang?: Lang) {
  return (tool.relatedTechniques || [])
    .map((slug) => getTechnique(slug, lang))
    .filter(Boolean)
    .map((t) => ({ title: t!.title, href: techniqueUrl(t!, lang), slug: t!.slug }))
}

export function categoryLabel(id: string): string {
  return artLabel(id)
}

export function artMeta(id: string, lang?: Lang) {
  const arts = toolsIndexFor(lang).arts || toolsIndexFor(lang).categories || []
  return arts.find((c) => c.id === id)
}

export function artLabel(id: string, lang?: Lang): string {
  const strings = getStrings()
  return artMeta(id, lang)?.label || (strings.tools.arts as Record<string, string>)[id] || id
}

export function artChip(id: string, lang?: Lang): string {
  const strings = getStrings()
  return artMeta(id, lang)?.chip || (strings.tools.chips as Record<string, string>)[id] || artLabel(id, lang)
}

export function sourceLabel(source?: string): string {
  const strings = getStrings()
  if (source === 'kali') return strings.technique.toolsKali
  if (source === 'defensive') return strings.technique.toolsDefensive
  return strings.technique.toolsCommon
}

export function extraTags(tool: ToolDbEntry): string[] {
  const skip = new Set([tool.kind, tool.source, tool.category, ...(tool.platforms || [])])
  return (tool.tags || []).filter((t) => t && !skip.has(t))
}

export function allExtraTagValues(lang?: Lang): string[] {
  const set = new Set<string>()
  for (const t of getAllTools(lang)) {
    for (const tag of extraTags(t)) set.add(tag)
  }
  return [...set].sort((a, b) => a.localeCompare(b, lang || getLang()))
}
